import * as StreamChat from 'stream-chat';

import { Client, Company } from 'src/store/clients/types';
import {
  getChannelsWithUser,
  getCompanyChannel,
} from 'src/utils/MessagingUtils';

interface PotentialWelcomeChannelInput {
  chatClient: StreamChat.StreamChat;
  clientCompany: Company | undefined;
  isDefaultChannelCompany: boolean;
  channel: StreamChat.Channel;
  activeClients: Map<string, Client>;
  companies: Map<string, Company>;
  setIsPotentialWelcomeChannel: (value: boolean) => void;
}

// TODO: when ALL individual channels have singleClientId field filled we can simply .find() the client from activeClients
export const getClientInIndividualChannel = (
  input: Pick<PotentialWelcomeChannelInput, 'activeClients' | 'channel'>,
) => {
  const membersInChannel = Object.keys(input.channel.state?.members || {}).map(
    // the format of memberID is portalID_userID, since portalID can also have underscores, we need access the last part of the string
    (memberID) => memberID.split('_').at(-1),
  );
  for (const memberId of membersInChannel) {
    if (!memberId) {
      continue;
    }
    const client = input.activeClients.get(memberId);
    if (client) {
      return client;
    }
  }
  return null;
};

export const getPotentialWelcomeChannel = async (
  input: PotentialWelcomeChannelInput,
) => {
  if (!input.chatClient) {
    return;
  }
  if (input.isDefaultChannelCompany) {
    // if the default channel type is "company"
    if (
      input.channel.data?.isWelcomeChannel ||
      input.channel.data?.membershipType === 'individual'
    ) {
      // single client channels or "isWelcomeChannel" channels (legacy single client channels) can be welcome channels IF company channel wasn't created
      const channelClient = await getClientInIndividualChannel(input);
      // TODO (@neil: many_companies): cannot rely on checking companyId
      // need to check companyIds
      const channelAssociatedCompanyId = channelClient?.fields?.companyId;
      if (channelAssociatedCompanyId) {
        const channelAssociatedCompany = input.companies.get(
          channelAssociatedCompanyId,
        );
        if (
          channelAssociatedCompany &&
          channelAssociatedCompany.fields.isPlaceholder
        ) {
          // check for placeholder company, if placeholder then individual channels are always potential welcome channels
          input.setIsPotentialWelcomeChannel(true);
          return;
        }
      }

      const companyChannels = channelClient?.fields?.companyId
        ? await getCompanyChannel(
            channelClient.fields.companyId,
            input.chatClient,
          )
        : [];
      // if no company channels were returned, the individual channel is a backup welcome message channel so set to true
      input.setIsPotentialWelcomeChannel(companyChannels.length === 0);
      return;
    }
    if (
      input.channel.data?.membershipType === 'company' &&
      !input.clientCompany?.fields?.isPlaceholder
    ) {
      // input.clientCompany means it is a company channel, and if it is not placeholder it is a potential welcome channel
      input.setIsPotentialWelcomeChannel(true);
      return;
    }
    // at this point in the function, the channel will be a group channel which is never a potential welcome channel
    input.setIsPotentialWelcomeChannel(false);
    return;
  }
  // if default channel is set to individual:

  const assignedClientIDs = Array.from(input.activeClients.keys());
  // if default channel is set to individual OR if the company is a placeholder channel...
  if (
    input.channel.data?.singleClientId ||
    input.channel.data?.isWelcomeChannel
  ) {
    input.setIsPotentialWelcomeChannel(true);
    return;
  }
  if (input.channel.data?.membershipType === 'company') {
    // if the current channel is a company channel, EVERY client in the company should have an individual channel
    // ...otherwise the company chan is a potential welcome message channel
    const getstreamMembersWithoutIndividualChannels: string[] = [];
    await Promise.all(
      // here we check each client in the channel to see if they have individual channels for welcome messages
      Object.keys(input.channel.state?.members || {}).map(async (memberID) => {
        if (!assignedClientIDs.includes(memberID.split('_')[2])) {
          return;
        }

        const channelsWithUser = await getChannelsWithUser(
          { memberId: memberID, companyId: null },
          input.chatClient,
        );

        const otherChannelsWithUser = channelsWithUser.filter(
          (c) => c.id !== input.channel.id,
        );

        if (otherChannelsWithUser.length === 0) {
          // if we don't find an individual channel we append to this array which is used to determine if company channel can be placeholder
          getstreamMembersWithoutIndividualChannels.push(memberID);
        }
      }),
    );
    if (getstreamMembersWithoutIndividualChannels.length > 0) {
      input.setIsPotentialWelcomeChannel(true);
      return;
    }
    input.setIsPotentialWelcomeChannel(false);
    return;
  }
  input.setIsPotentialWelcomeChannel(false);
};
